import { lookupPersonByEmail } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { parseBEDateIgnoreTimezone } from '@dealstack/shared/lib/format/dates/date';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { controllingPartyFieldName } from './controllingParties.utils';

/**
 * Resolves an existing person in the deal directory by email and pre-fills the
 * controlling party's name, address and tax-residence fields (MIP-2558 AC:
 * "email lookup resolves existing persons in the deal directory; if found,
 * the data is pre-filled"). Best-effort: lookup failures are swallowed so they
 * never block manual entry.
 */
export const useControllingPartyEmailLookup = () => {
	// Untyped context so the dynamic `controllingParties.<index>.<field>` paths
	// are accepted by setValue.
	const { setValue } = useFormContext();
	const [isLooking, setIsLooking] = useState(false);

	const prefillFromEmail = async (index: number, email: string): Promise<void> => {
		const trimmedEmail = email?.trim();
		if (!trimmedEmail) {
			return;
		}

		setIsLooking(true);
		try {
			const result = await lookupPersonByEmail(trimmedEmail);

			if (!result?.found) {
				return;
			}

			if (result.firstName) {
				setValue(controllingPartyFieldName(index, 'firstName'), result.firstName);
			}
			if (result.lastName) {
				setValue(controllingPartyFieldName(index, 'lastName'), result.lastName);
			}
			if (result.dateOfBirth) {
				setValue(
					controllingPartyFieldName(index, 'dateOfBirth'),
					parseBEDateIgnoreTimezone(result.dateOfBirth)
				);
			}
			if (result.placeOfBirth) {
				setValue(controllingPartyFieldName(index, 'placeOfBirth'), result.placeOfBirth);
			}
			if (result.countryOfBirth) {
				setValue(controllingPartyFieldName(index, 'countryOfBirth'), result.countryOfBirth);
			}
			if (result.citizenship?.length) {
				setValue(controllingPartyFieldName(index, 'citizenship'), result.citizenship);
			}
			if (result.address) {
				setValue(controllingPartyFieldName(index, 'country'), result.address.country ?? '');
				setValue(controllingPartyFieldName(index, 'city'), result.address.city ?? '');
				setValue(
					controllingPartyFieldName(index, 'postCode'),
					result.address.postCode ?? ''
				);
				setValue(controllingPartyFieldName(index, 'street'), result.address.street ?? '');
				setValue(
					controllingPartyFieldName(index, 'houseNumber'),
					result.address.houseNumber ?? ''
				);
			}
			if (result.taxResidences?.length) {
				setValue(
					controllingPartyFieldName(index, 'taxResidences'),
					result.taxResidences.map((residence) => ({
						taxResidency: residence.taxResidency,
						tin: residence.tin ?? '',
					}))
				);
			}
		} catch {
			// Prefill is best-effort; ignore lookup failures and let the investor
			// fill the fields manually.
		} finally {
			setIsLooking(false);
		}
	};

	return { prefillFromEmail, isLooking };
};
